

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {IErrors} from "@/model/IErrors";
import {ILocker} from "@/model/Locker/lockerInterfaces";
import {IOnOffHandlers} from "bonomo/dist";
import {IDiffusionExpressOptions, IUserImported} from "@/store/modules/userDiffusionExpressModule";
import DiffusionExpressOverrideDialog from "@/components/user/DiffusionExpressOverrideDialog.vue";
import DiffusionExpressResults from "@/components/user/DiffusionExpressResults.vue";
import {EDiffusionExpressCode} from "@/model/User/EDiffusionExpressCode";
import {EPermissionActions} from "@/model/EPermissionActions";

const lockerModule = namespace("lockerModule");
const userDiffusionExpressModule = namespace("userDiffusionExpressModule");

@Component({
  components: {
    DiffusionExpressOverrideDialog,
    DiffusionExpressResults,
  },
})
export default class DiffusionExpress extends Vue {

  @userDiffusionExpressModule.Action("fetchImportLockers")
  private fetchImportLockers!: () => void;
  // @userDiffusionExpressModule.Action("exportUsers")
  // private exportUsers!: () => void;
  @userDiffusionExpressModule.Getter('importLockers')
  private lockers!: ILocker[];
  @userDiffusionExpressModule.Getter('importResults')
  private importResults!: IUserImported[];
  @userDiffusionExpressModule.Getter('importPreview')
  private importPreview!: IUserImported[];
  @userDiffusionExpressModule.Getter('errors')
  private errors!: IErrors;

  protected readonly OKstatuses: number[] = [
    EDiffusionExpressCode.HEADER_OK,
    EDiffusionExpressCode.NEW_EMAIL,
    EDiffusionExpressCode.UPDATED,
    EDiffusionExpressCode.NOT_MODIFIED,
    EDiffusionExpressCode.RESTORED,
    EDiffusionExpressCode.WOULD_RESTORE,
    EDiffusionExpressCode.DEACTIVATED,
    EDiffusionExpressCode.WOULD_DEACTIVATE,
    EDiffusionExpressCode.NOT_DEACTIVATED_ACTIVE_ORDERS,
    EDiffusionExpressCode.VALID_LINE,
    EDiffusionExpressCode.RESIDENT_LINE,
  ];

  protected get canOverrideLockerUsers(): boolean {
    return this.$canAction(EPermissionActions.ACTION_USERS_DIFFUSION_EXPRESS_OVERRIDE);
  }


  protected get exampleLink(): string {
    return '/modulo/assets/resources/importExample-' + this.languageSelected + '.csv'
  }
  protected get hasImportResults(): boolean {
    return this.importResults && !!this.importResults.length;
  }
  protected get hasErrors(): boolean {
    return this.errors && !!Object.keys(this.errors).length;
  }
  protected get showResults(): boolean {
    return this.hasImportResults || this.hasErrors;
  }
  protected rules: any[] = [];
  protected file: any = null;
  protected lockerSelected: number = 0;
  protected languageSelected: string = 'es';
  protected ignorePhone: boolean = false;
  protected overrideLockerUsers: boolean = false;
  protected overrideDialogShowing: boolean = false;
  protected languageCodes: string[] = ['en', 'es', 'ca', ];
  protected loading: boolean = true;
  protected submitting: boolean = false;

  private subscribes: IOnOffHandlers = {};

  // created / beforeDestroy are always the first
  created() {

    const l = this.$i18n.locale;
    this.languageSelected = this.languageCodes.includes(l) ? l : 'es';

    // one reason we don't really use vue's validation are the lame rules.
    this.rules.push(
        (v: any) => !!v || this.$t('errors.general.required'),
    );

    this.subscribes = this.$ons({
      'login.OK': this.loadData,
      'backend.call.OK.importLockersLoaded': this.lockersLoaded,
      'backend.call.OK.usersImported': this.imported,
      'backend.call.KO.usersImported': this.imported,
      'backend.call.OK.importPreviewLoaded': this.previewLoaded,
      'backend.call.KO.importPreviewLoaded': this.previewFailed,
      // 'backend.call.OK.exportUsersLoaded': this.exported,
    });

    this.loadData();

  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  addFile(file: any){
    this.file = file;
  }

  findLocker(lockerId: number): ILocker {
    const locker = this.lockers.find((each: ILocker) => each.id == lockerId);
    return locker ? locker : {} as ILocker;
  }

  imported() {
    this.loading = false;
    this.submitting = false;
    this.overrideDialogShowing = false;
    // overriding must be opted in again on every run
    this.overrideLockerUsers = false;
    // clean file, and reset validation so it won't turn red by being null
    this.file = null;
    (this.$refs.form as any).resetValidation();
  }

  loadData() {
    this.loading = true;
    this.fetchImportLockers();
  }

  lockersLoaded() {
    this.loading = false;
    if (this.lockers && (this.lockers.length === 1)) {
      this.lockerSelected = this.lockers[0].id;
    }
  }

  onSubmit(){

    // @ts-ignore (another reason why we don't like v-form :) )
    if (this.$refs.form.validate()) {
      this.loading = true;
      this.submitting = true;
      // overriding (deactivating residents not in the CSV) always goes through the preview + confirmation dialog first
      this.$store.dispatch(
        'userDiffusionExpressModule/' + (this.overrideLockerUsers ? 'previewImport' : 'importUsers'),
        this.importOptions()
      );
    }

  }

  previewLoaded() {
    this.loading = false;
    this.submitting = false;
    this.overrideDialogShowing = true;
  }

  previewFailed() {
    this.loading = false;
    this.submitting = false;
  }

  onOverrideConfirmed() {
    this.loading = true;
    this.submitting = true;
    this.$store.dispatch('userDiffusionExpressModule/importUsers', this.importOptions());
  }

  onOverrideCancelled() {
    this.overrideDialogShowing = false;
  }

  private importOptions(): IDiffusionExpressOptions {
    return {
      file: this.file,
      lockerId: this.lockerSelected,
      languageCode: this.languageSelected,
      ignorePhone: this.ignorePhone,
      overrideLockerUsers: this.overrideLockerUsers,
    };
  }

}

